import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { get, authPostFormData } from '../../services/apiService';
import { getCurrentUser } from '../../services/authService';

const darkGreenColor = "#1D4434";

/** Circular progress with percentage in center; color by completion (red / amber / green). */
function ProgressDonut({ percentage = 0, size = 120 }) {
  const value = Math.min(Math.max(Number(percentage), 0), 100);
  const color = value < 33 ? '#d32f2f' : value < 67 ? '#ed6c02' : darkGreenColor;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={4}
        sx={{
          color,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round'
          }
        }}
      />
      <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2, color }}>
          {Math.round(value)}%
        </Typography>
        <Typography variant="caption" color="text.secondary">complete</Typography>
      </Box>
    </Box>
  );
}

/**
 * HIPAA/CUI Environment Build - Track environment build progress from Excel uploads
 * @returns {JSX.Element} HipaaCuiEnvironmentBuild component
 */
const HipaaCuiEnvironmentBuild = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [buildStatus, setBuildStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadData();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    try {
      const user = getCurrentUser();
      setCurrentUser(user);
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await get('/api/hipaa-cui/environment-build');
      
      if (response.success) {
        setBuildStatus(response.data);
      }
    } catch (err) {
      console.error('Error loading environment build status:', err);
      setError(err.message || 'Failed to load environment build status');
    } finally {
      setLoading(false);
    }
  };

  const isYogeshPatel = () => {
    if (!currentUser) return false;
    const username = currentUser.username || currentUser.email || '';
    return username.toLowerCase().includes('yogeshpatel');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await authPostFormData('/api/hipaa-cui/environment-build/upload', formData);

      if (response.success) {
        setSuccessMessage('Excel file uploaded successfully! Status updated.');
        await loadData();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(response.message || 'Failed to upload file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload Excel file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h6">
          Environment Build Progress
        </Typography>
        {isYogeshPatel() && (
          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={uploading}
            sx={{
              backgroundColor: darkGreenColor,
              '&:hover': { backgroundColor: '#0f2e1f' }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload Excel File'}
            <input
              type="file"
              accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {!buildStatus ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No environment build status data available.
          </Typography>
          {isYogeshPatel() && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please upload an Excel file to get started. The file should have completed status in cell B24 and remaining status in cell B25.
            </Typography>
          )}
        </Paper>
      ) : (
        <>
          {/* Single compact progress card: donut + stats row + bar + upload info */}
          <Card sx={{ mb: 2, overflow: 'visible' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                gap={{ xs: 2, sm: 3 }}
                flexWrap="wrap"
              >
                <ProgressDonut
                  percentage={buildStatus.completionPercentage || 0}
                  size={96}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8, fontSize: '0.7rem' }}>
                    Overall progress
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: darkGreenColor, mt: 0.25 }}>
                    {Math.round(buildStatus.completedStatus || 0)} of {Math.round(buildStatus.totalStatus || 0)} completed
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {Math.round(buildStatus.completedStatus || 0)} done
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PendingIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {Math.round(buildStatus.remainingStatus || 0)} left
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total: {Math.round(buildStatus.totalStatus || 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.round(buildStatus.completionPercentage || 0)}
                sx={{
                  mt: 2,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: `${darkGreenColor}20`,
                  '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: darkGreenColor }
                }}
              />
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {buildStatus.uploadedAt ? new Date(buildStatus.uploadedAt).toLocaleString() : 'N/A'}
                  {buildStatus.uploadedBy?.username && ` by ${buildStatus.uploadedBy.username}`}
                </Typography>
                {buildStatus.fileName && (
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.9 }}>
                    · {buildStatus.fileName}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Major Milestones – always visible */}
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Major Milestones
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                From uploaded sheet &quot;Major Milestones&quot;
              </Typography>
              {buildStatus.milestones && buildStatus.milestones.length > 0 ? (
                <TableContainer sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader aria-label="Major milestones">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100', whiteSpace: 'nowrap', minWidth: 100 }}>
                          Week of
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100', minWidth: 160 }}>
                          Planned
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100', minWidth: 140 }}>
                          Actual
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100', minWidth: 180 }}>
                          Risks / Blockers / Dependencies
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100', minWidth: 160 }}>
                          Owners / Notes
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...(buildStatus.milestones || [])]
                        .sort((a, b) => {
                          const da = a.weekOf ? new Date(a.weekOf).getTime() : 0;
                          const db = b.weekOf ? new Date(b.weekOf).getTime() : 0;
                          return da - db;
                        })
                        .map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {row.weekOf
                                ? new Date(row.weekOf).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {row.planned || '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {row.actual || '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {row.risksBlockersDependencies || '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {row.ownersNotes || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper variant="outlined" sx={{ py: 3, px: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No milestones in the uploaded file. Add a &quot;Major Milestones&quot; sheet with columns: Week of, Planned, Actual, Risks/Blockers/Dependencies, Owners/Notes.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default HipaaCuiEnvironmentBuild;

